import { useState, useEffect, memo, useCallback } from "react";
import { CSVLink } from "react-csv";
import AnimacionCarga from "./animacion_de_carga";
import TablaGenerica from "./tablas";
import BotonSOS from "./sos/boton_sos";
import { consultarSimple, crearRegistro, actualizarRegistro, eliminarRegistro } from "../../functions/crud";
import Swal from "sweetalert2";
import "bootstrap-icons/font/bootstrap-icons.css";

// Correos de Coordinar que reciben un inmueble administrativo en cada conjunto.
// El backend (api/inmuebles_sos_unificado.js) lee esta rama.
const RUTA = "correos_sos";

// ADMINISTRADOR: un inmueble ADMINISTRADOR por conjunto.
// SUPER: cuatro inmuebles por conjunto (ADMINISTRADOR, SUPER, GUARDA y RESIDENTE).
const ROLES = ["ADMINISTRADOR", "SUPER"];

const esSuper = () => localStorage.getItem("Permisos") === "SUPER";

const columnas = [
  { label: "Correo", key: "correo" },
  { label: "Rol", key: "rol" },
];

const estiloCSV = { padding: "7px 14px", margin: 2, backgroundColor: "#c0392b", color: "#fff", borderRadius: 7, fontSize: "0.82rem", fontWeight: 500, textDecoration: "none" };

const escaparHtml = (texto = "") =>
  String(texto).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const InmueblesAdministrativos = memo(() => {
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const capturarDatos = useCallback(async () => {
    try {
      const data = await consultarSimple(RUTA);
      const lista = Object.entries(data && typeof data === "object" ? data : {}).map(([clave, registro]) => ({
        clave,
        correo: registro?.correo || "",
        rol: registro?.rol === "SUPER" ? "SUPER" : "ADMINISTRADOR",
      }));
      setDatos(lista);
    } catch (e) {
      console.error(e);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    capturarDatos();
  }, [capturarDatos]);

  const formularioHtml = (registro = {}) => `
    <style>
      #ia-form .swal2-input {
        margin: 0 !important; width: 100% !important; box-sizing: border-box !important;
        padding: 10px !important; border: 1px solid #d1d5db !important;
        border-radius: 5px !important; font-size: 14px !important;
      }
      #ia-form label { display:block; margin-bottom:6px; color:#555; font-size:14px; font-weight:500; }
      #ia-form .ia-field { margin-bottom:15px; }
    </style>
    <div id="ia-form" style="text-align:left;padding:10px 20px 0;">
      <div class="ia-field">
        <label>Correo electrónico *</label>
        <input id="ia-correo" type="email" autocomplete="off" class="swal2-input" placeholder="ejemplo@coordinarseguridad.com" value="${escaparHtml(registro.correo)}">
      </div>
      <div class="ia-field">
        <label>Rol</label>
        <select id="ia-rol" class="swal2-input">
          ${ROLES.map((r) => `<option value="${r}" ${(registro.rol || "ADMINISTRADOR") === r ? "selected" : ""}>${r}</option>`).join("")}
        </select>
        <small style="display:block;margin-top:6px;color:#7f8c8d;">SUPER recibe en cada conjunto los permisos ADMINISTRADOR, SUPER, GUARDA y RESIDENTE.</small>
      </div>
    </div>
  `;

  const leerFormulario = (claveActual = null) => () => {
    const correo = document.getElementById("ia-correo").value.trim().toLowerCase();
    const rol = document.getElementById("ia-rol").value;

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) {
      Swal.showValidationMessage("Ingresa un correo electrónico válido");
      return false;
    }
    if (datos.some((d) => d.correo.toLowerCase() === correo && d.clave !== claveActual)) {
      Swal.showValidationMessage("Este correo ya está en la lista");
      return false;
    }
    return { correo, rol };
  };

  // Con `base` abre el formulario con los datos actuales de otro registro (Duplicar).
  const agregarCorreo = async (base = null) => {
    const { value: formValues } = await Swal.fire({
      title: base ? "Duplicar Correo" : "Agregar Correo",
      html: formularioHtml(base ? { correo: base.correo, rol: base.rol } : {}),
      showCancelButton: true,
      confirmButtonText: "Agregar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
      width: 550,
      preConfirm: leerFormulario(),
    });

    if (!formValues) return;

    try {
      await crearRegistro(RUTA, { ...formValues, fechaCreacion: new Date().toISOString() });
      await Swal.fire({ icon: "success", title: "Correo agregado", timer: 2000, showConfirmButton: false });
      capturarDatos();
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo agregar el correo.", confirmButtonColor: "#c0392b" });
    }
  };

  const editarCorreo = async (fila) => {
    const { value: formValues } = await Swal.fire({
      title: "Editar Correo",
      html: formularioHtml(fila),
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
      width: 550,
      preConfirm: leerFormulario(fila.clave),
    });

    if (!formValues) return;

    try {
      await actualizarRegistro(`${RUTA}/${fila.clave}/correo`, formValues.correo);
      await actualizarRegistro(`${RUTA}/${fila.clave}/rol`, formValues.rol);
      await Swal.fire({ icon: "success", title: "Actualizado", timer: 2000, showConfirmButton: false });
      capturarDatos();
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo actualizar.", confirmButtonColor: "#c0392b" });
    }
  };

  const eliminarCorreo = async (fila) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar correo?",
      html: `
        <div style="text-align:left;padding:15px;">
          <div style="background:#f8f9fa;padding:15px;border-radius:8px;border-left:4px solid #c0392b;">
            <strong style="display:block;color:#2c3e50;">${escaparHtml(fila.correo)}</strong>
          </div>
          <p style="margin-top:15px;color:#c0392b;">Dejará de recibir los inmuebles administrativos la próxima vez que se ejecute el proceso.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c0392b",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!isConfirmed) return;

    try {
      await eliminarRegistro(`${RUTA}/${fila.clave}`);
      Swal.fire({ title: "Eliminado", icon: "success", timer: 2000, showConfirmButton: false });
      capturarDatos();
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar el correo.", confirmButtonColor: "#c0392b" });
    }
  };

  const botonesAccion = [
    { texto: "Editar", icono: "bi-pencil", accion: editarCorreo },
    { texto: "Duplicar", icono: "bi-copy", accion: agregarCorreo },
    { texto: "Eliminar", icono: "bi-trash", accion: eliminarCorreo },
  ];

  if (!esSuper()) {
    return <p style={{ padding: 24, fontSize: 16 }}>Este módulo solo está disponible para usuarios con permiso SUPER.</p>;
  }

  return cargando ? (
    <AnimacionCarga />
  ) : (
    <>
      <TablaGenerica
        datos={datos}
        titulos={columnas.map((c) => c.label.toUpperCase())}
        campos={columnas.map((c) => c.key)}
        elementosPorPagina={500}
        botones={botonesAccion}
        contenidoExtra={
          <div style={{ marginLeft: "auto", display: "flex", gap: 0 }}>
            <button onClick={capturarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
            <CSVLink data={datos} headers={columnas} filename="inmuebles_administrativos.csv" separator=";" style={estiloCSV} title="Exportar"><i className="bi bi-download" /></CSVLink>
            <button onClick={() => agregarCorreo()} title="Agregar Correo"><i className="bi bi-plus-lg" /></button>
          </div>
        }
      />
      <BotonSOS />
    </>
  );
});

export default InmueblesAdministrativos;
